<template>
  <nav>
    <v-card rounded="xl" class="pa-6" elevation="4" min-height="200">
      <bubble-menu
        :editor="editor"
        :tippy-options="{ duration: 100 }"
        v-if="editor"
      >
        <v-btn @click="editor.chain().focus().toggleBold().run()" small :class="{ 'v-btn--active': editor.isActive('bold') }" class="py-4 rounded-r-0 rounded-l-lg greytext--text">
          <v-icon>mdi-format-bold</v-icon>
        </v-btn>
        <v-btn @click="editor.chain().focus().toggleItalic().run()" small :class="{ 'v-btn--active': editor.isActive('italic') }" class="py-4 rounded-0 greytext--text">
          <v-icon>mdi-format-italic</v-icon>
        </v-btn>
        <v-btn v-if="editor.extensionStorage.hasOwnProperty('custom-link')" small :class="{ 'v-btn--active': editor.isActive('custom-link') }" class="py-4 rounded-0 greytext--text"
          @click="dialogVisibleURL = true"
        >
          <v-icon>mdi-link</v-icon>
        </v-btn>
        <v-btn v-if="editor.isActive('custom-link') && editor.extensionStorage.hasOwnProperty('custom-link')" small :class="{ 'v-btn--active': editor.isActive('custom-link') }" class="py-4 rounded-0 greytext--text"
          @click="removeLink"
        >
          <v-icon>mdi-link-off</v-icon>
        </v-btn>
        <v-btn @click="editor.chain().focus().setTextAlign('left').run()" small :class="{ 'v-btn--active': editor.isActive({ textAlign: 'left' }) }" class="py-4 rounded-0 greytext--text">
          <v-icon>mdi-format-align-left</v-icon>
        </v-btn>
        <v-btn @click="editor.chain().focus().setTextAlign('center').run()" small :class="{ 'v-btn--active': editor.isActive({ textAlign: 'center' }) }" class="py-4 rounded-0 greytext--text">
          <v-icon>mdi-format-align-center</v-icon>
        </v-btn>
        <v-btn @click="editor.chain().focus().setTextAlign('right').run()" small :class="{ 'v-btn--active': editor.isActive({ textAlign: 'right' }) }" class="py-4 rounded-l-0 rounded-r-lg greytext--text">
          <v-icon>mdi-format-align-right</v-icon>
        </v-btn>
      </bubble-menu>
      <editor-content
        :editor="editor"
      />
    </v-card>
    <ConfirmationDialogLink 
      v-if="dialogVisibleURL" 
      :visible="dialogVisibleURL" 
      :url="editor.getAttributes('custom-link').href"
      data-testid="confirmation-dialog-link" 
      @close="dialogVisibleURL = false" 
      @proceedSubmit="setLinkHref" 
    />
  </nav>
</template>
<script>
import { Editor, EditorContent, BubbleMenu } from '@tiptap/vue-2'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Underline from '@tiptap/extension-underline'
import BulletList from '@tiptap/extension-bullet-list'
import OrderedList from '@tiptap/extension-ordered-list'
import ListItem from '@tiptap/extension-list-item'
import FontSize from './editor_extensions/font-size.js'
//import Div from './editor_extensions/div.js'
import Strike from '@tiptap/extension-strike'
import HardBreak from '@tiptap/extension-hard-break'
import History from '@tiptap/extension-history'
import CustomLink from './editor_extensions/custom-link.js'
import TextAlign from '@tiptap/extension-text-align'
import ConfirmationDialogLink from './ConfirmationDialogLink.vue'

export default {
    components: {
        EditorContent,
        BubbleMenu,
        ConfirmationDialogLink
    },
    props:{
        content: {
          type: String,
          default: ''
        },
        template: {
          type: Object,
          default: () => {}
        }
       
    },
    data(){
      return{
        editor: null,
        oldContent: this.content,
        dialogVisibleURL: false
      }
    },
    created(){
    },
    mounted() {
      //override paragraph node - was not working in separate file
      const CustomParagraph = Paragraph.extend({
        addCommands() {
          return {
            setParagraph: attributes => ({ commands }) => {
              return commands.setNode('paragraph', attributes);
            },
          }
        }
      })

      const CustomTextStyle = TextStyle.extend({
        addAttributes() {
          return {
            class: {
                default: '',
                rendered: true
            }
          }
        },
      })

    this.editor = new Editor({
      editorProps: {
        handleClick: ( ) => {
          this.setActiveEditor(this.editor)
        },
        handleDoubleClick: ( ) => {
            this.editor.setEditable(true)
        },
        handleDOMEvents: {
            drop: (view, e) => { e.preventDefault(); },
        },
      },
      // hide the drop position indicator
      dropCursor: { width: 0, color: 'transparent' },
      extensions: [
        Document,
        Text,
        CustomParagraph,
        CustomTextStyle,
        Color,
        FontSize,
        History,
        HardBreak,
         CustomLink.configure({
          openOnClick: false,
          HTMLAttributes: {
            target: '_blank',
          },
        }),
        Highlight,
        Italic,
        ListItem,
        OrderedList,
        Bold,
        Strike,
        Underline,
        BulletList,
        TextAlign.configure({
          types: ['paragraph'],
        }),
      ],
      content: this.content,
      //editable: false,
      onUpdate: ({ editor}) => {
        const newContent = editor.getHTML()
        this.$emit('updateContent', newContent, this.template)
      },
      onSelectionUpdate: ({ editor}) => {
        this.$emit('proceedClick',editor)
        const { view: { state: { tr: { selection } } } } = editor
        this.$emit('proceedMark', !selection.empty)
      }

    })
  },

  beforeUnmount() {
    this.editor.destroy()
  },
   methods: {
    setActiveEditor(editor){
      this.$emit('proceedClick',editor)
    },
    setLinkHref(url) {
      // empty
      if (url === '') {
        this.editor.chain().focus().extendMarkRange('custom-link').unsetLink().run()
      } else {
        // update link
        this.editor.chain().focus().extendMarkRange('custom-link').setLink({ href: url }).run()
      }
      this.dialogVisibleURL = false
    },
    removeLink() {
      this.editor.chain().focus().extendMarkRange('custom-link').unsetLink().run()
    },
  }
}
</script>